import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { domainForLegacyTab } from "./modules/substantive/domains";
import { ChevronDown, ChevronRight, Search, X, PanelLeftClose, PanelLeftOpen, Menu, ExternalLink } from "lucide-react";
import { confirmLeave } from "@/lib/unsavedChanges";

// The engagement's tools in the ten groups of MASTER ROADMAP FINAL V3 phase 2, in the order an audit is
// performed. Every panel keeps its value, its label and its data-testid, so a ?tab= deep link, a legacy link
// (?tab=sales and friends) and every recorded training route resolve as before, and no panel gains or loses
// access — authorisation stays with the server and each module.
//
// Two entries are not engagement panels but links to the firm-level page that owns the function: the
// Standards Library (which offers the way back to this engagement and panel) and Backup & Pemulihan (Super
// Admin only, the same rule as that page and its API). The AI Audit Assistant is no longer a panel: it is
// the global help button (AssistantButton), and ?tab=ai opens it.
export const NAV_GROUPS = [
  { key: "overview", label: "Ikhtisar", tabs: [["overview", "Ikhtisar"]] },
  { key: "acceptance", label: "Penerimaan", tabs: [["acceptance", "Penerimaan"], ["team", "Tim & Independensi"]] },
  { key: "data", label: "Data & PBC", tabs: [["pbc", "PBC"], ["dataintake", "Data Intake"], ["mapping", "TB / Mapping"], ["evidence", "Pusat Bukti"], ["lineage", "Lineage Bukti"]] },
  { key: "planning", label: "Perencanaan", tabs: [["materiality", "Materialitas"], ["risk", "Penilaian Risiko"], ["strategy", "Strategi Audit"], ["perencanaan-risiko", "Perencanaan & Risiko"]] },
  { key: "execution", label: "Pelaksanaan Audit", tabs: [["areas", "Area Audit"], ["walkthrough", "Walkthrough"], ["toc", "Test of Control"], ["sampling", "Sampling"], ["jet", "JET"], ["substantive", "Pengujian Substantif"], ["bank", "Rekonsiliasi Bank"], ["wht", "Pajak Dipotong / Dipungut"], ["fraud", "Fraud"], ["confirmations", "Konfirmasi"], ["relatedparty", "Pihak Berelasi"]] },
  { key: "workpapers", label: "Kertas Kerja", tabs: [["accounts", "Kertas Kerja Akun"], ["ewp", "Program & Kertas Kerja"], ["audit-index", "Indeks A–E"]] },
  { key: "completion", label: "Penyelesaian", tabs: [["completion", "Penyelesaian"], ["reporting", "FS / CALK / Laporan"]] },
  { key: "quality", label: "Mutu & Group", tabs: [["quality", "Kualitas & Grup"], ["gates", "Gerbang Kualitas"]] },
  { key: "standards", label: "Standar", tabs: [], links: [{ key: "standards-library", label: "Pustaka Standar", to: ({ id, tab }) => `/standards-library?engagement=${encodeURIComponent(id)}&tab=${encodeURIComponent(tab)}` }] },
  { key: "archive", label: "Arsip & Admin", tabs: [["binder", "Binder Audit"], ["exports", "Pusat Ekspor"], ["archive", "Arsip & Roll-Forward"]], links: [{ key: "backup-recovery", label: "Backup & Pemulihan", to: () => "/admin/backup-recovery", roles: ["Super Admin"] }] },
];

// One line under each panel title: what the panel is for, and how it differs from its neighbour where the
// names overlap (docs/navigation/MENU_MATRIX.md).
export const PANEL_PURPOSE = {
  overview: "Ringkasan metrik, tahapan dan sign-off perikatan.",
  acceptance: "Penerimaan & keberlanjutan klien sebelum perikatan dimulai.",
  team: "Susunan tim, penugasan dan deklarasi independensi.",
  pbc: "Daftar permintaan dokumen klien (PBC) dan statusnya.",
  dataintake: "Unggah dan validasi data klien (TB, GL, subledger), termasuk pemetaan kolom sumber per dataset.",
  mapping: "Satu pintu pemetaan: COA/TB → pos LK & area, reviu COA, pemetaan kolom sumber, area/siklus, dan kertas kerja.",
  evidence: "Semua bukti audit perikatan, dengan filter fase dan modul.",
  lineage: "Jejak asal-usul bukti dari sumber hingga kertas kerja.",
  materiality: "Materialitas keseluruhan, pelaksanaan, ambang remeh, dan materialitas spesifik.",
  risk: "Register risiko salah saji material per asersi: identifikasi, penilaian dan respons tiap risiko.",
  strategy: "Strategi audit keseluruhan dan pendekatan per area.",
  "perencanaan-risiko": "Dasbor binder perencanaan (bagian A & B): kesiapan kertas kerja, tolok ukur materialitas, akun signifikan, cakupan siklus dan ekspor. Pengisian dilakukan di Materialitas, Penilaian Risiko dan Strategi Audit.",
  areas: "Area audit (siklus) beserta akun dan prosedurnya.",
  walkthrough: "Walkthrough proses dan pengendalian.",
  toc: "Pengujian pengendalian (Test of Control).",
  sampling: "Rancangan dan evaluasi sampel.",
  jet: "Pengujian jurnal (JET) dan lineage jurnal.",
  substantive: "Pengujian substantif per domain (penjualan, pembelian, persediaan, beban).",
  bank: "Rekonsiliasi bank dan rekening koran.",
  wht: "Rekonsiliasi pajak dipotong / dipungut.",
  fraud: "Prosedur dan inkuiri risiko kecurangan.",
  confirmations: "Konfirmasi eksternal.",
  relatedparty: "Identifikasi dan pengujian pihak berelasi.",
  accounts: "Tampilan per akun: risiko, prosedur, bukti, kesimpulan dan sign-off satu akun.",
  ewp: "Tampilan per prosedur: program audit dan kertas kerja elektronik tiap prosedur.",
  "audit-index": "Indeks kertas kerja A–E dan ekspor indeks E.",
  completion: "Prosedur penyelesaian, peristiwa kemudian, representasi.",
  reporting: "Laporan keuangan, CALK dan laporan auditor.",
  quality: "Manajemen mutu, EQR dan audit grup/komponen.",
  gates: "Gerbang kualitas per tahap dan penghambatnya.",
  binder: "Susunan dan kelengkapan binder induk, keputusan pemetaan kertas kerja, dan PDF binder.",
  exports: "Unduhan dan antrean ekspor (Excel, Word, PDF) perikatan.",
  archive: "Penguncian arsip, retensi, dan roll-forward ke periode berikutnya.",
};

export const ALL_TABS = NAV_GROUPS.flatMap(g => g.tabs.map(([value]) => value));

export const groupOf = (tab) => NAV_GROUPS.find(g => g.tabs.some(([value]) => value === tab)) || null;

export const labelOf = (tab) => {
  for (const g of NAV_GROUPS) for (const [value, label] of g.tabs) if (value === tab) return label;
  return null;
};

// The open panel lives in the URL (?tab=), so a refresh reopens it and a link shares it. Each change is a new
// history entry, so Back and Forward walk the panels. The four substantive domains used to be panels; their
// old values (?tab=sales …) open the consolidated tool on that domain. ?tab=ai used to be the AI Assistant
// panel and now opens the global assistant over the overview; any other unknown value shows the overview.
// Leaving a panel with unsaved edits asks first; setTab returns false when the user stays.
export function useEngagementTab() {
  const [sp, setSp] = useSearchParams();
  const requested = sp.get("tab") || "overview";
  const legacyDomain = domainForLegacyTab(requested);
  const tab = legacyDomain ? "substantive" : ALL_TABS.includes(requested) ? requested : "overview";
  const [assistantOpen, setAssistantOpen] = useState(0);
  useEffect(() => { if (requested === "ai") setAssistantOpen((n) => n + 1); }, [requested]);
  const setTab = (value) => {
    if (value === tab && !legacyDomain) return true;
    if (!confirmLeave()) return false;
    setSp(value === "overview" ? {} : { tab: value });
    window.scrollTo?.({ top: 0 });
    return true;
  };
  return { tab, legacyDomain, setTab, assistantOpen, sp, setSp };
}

const STORE = "auditos.engagementNav.v1";
const readStore = () => { try { return JSON.parse(window.localStorage.getItem(STORE)) || {}; } catch { return {}; } };
const writeStore = (value) => { try { window.localStorage.setItem(STORE, JSON.stringify(value)); } catch { /* a convenience only */ } };

const normalise = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// Pending counts come only from what the engagement already reports; nothing is estimated here.
export function pendingCounts(eng) {
  const metrics = eng?.gates?.metrics || {};
  const blockedStages = Object.values(eng?.gates?.stages || {}).filter(s => s && s.can_proceed === false).length;
  return { risk: metrics.significant_without_response || 0, ewp: metrics.open_procedures || 0, gates: blockedStages };
}

export function NavList({ id, tab, onSelect, role, pending = {}, compact, onFollowLink }) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => readStore().collapsed || {});
  const active = groupOf(tab);
  const q = normalise(query.trim());
  const groups = useMemo(() => NAV_GROUPS.map(g => {
    const links = (g.links || []).filter(l => !l.roles || l.roles.includes(role));
    const match = (label) => !q || normalise(label).includes(q) || normalise(g.label).includes(q);
    return { ...g, tabs: g.tabs.filter(([, label]) => match(label)), links: links.filter(l => match(l.label)) };
  }).filter(g => g.tabs.length || g.links.length), [q, role]);
  const toggle = (key) => {
    const next = { ...collapsed, [key]: !collapsed[key] };
    setCollapsed(next); writeStore({ ...readStore(), collapsed: next });
  };
  const item = compact ? "min-h-11 text-sm" : "min-h-9 text-[13px]";
  return (
    <nav aria-label="Navigasi perikatan" className="flex flex-col min-h-0">
      <label className="relative block mb-2">
        <span className="sr-only">Cari menu perikatan</span>
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input type="search" data-dirty-ignore value={query} onChange={e => setQuery(e.target.value)} placeholder="Cari menu…"
          className={`w-full border border-border rounded-sm pl-7 pr-8 bg-white ${compact ? "h-11 text-sm" : "h-8 text-xs"}`} data-testid="engagement-nav-search" />
        {query && <button type="button" aria-label="Hapus pencarian" className="absolute right-1 top-1/2 -translate-y-1/2 p-1" onClick={() => setQuery("")}><X size={14} /></button>}
      </label>
      {groups.length === 0 && <p className="text-xs text-muted-foreground px-1" data-testid="engagement-nav-empty">Tidak ada menu yang cocok.</p>}
      <div className="space-y-1">
        {groups.map(g => {
          const open = !!q || !collapsed[g.key] || active?.key === g.key;
          return (
            <div key={g.key} data-testid={`nav-group-${g.key}`}>
              <button type="button" onClick={() => toggle(g.key)} aria-expanded={open} data-testid={`nav-group-toggle-${g.key}`}
                className={`w-full flex items-center justify-between px-2 ${compact ? "min-h-11" : "min-h-8"} text-[11px] font-semibold uppercase tracking-wide rounded-sm ${active?.key === g.key ? "text-navy-700" : "text-muted-foreground"} hover:bg-slate-50`}>
                <span>{g.label}</span>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {open && (
                <ul className="pl-1">
                  {g.tabs.map(([value, label]) => {
                    const current = value === tab;
                    const count = pending[value] || 0;
                    return (
                      <li key={value}>
                        <button type="button" onClick={() => onSelect(value)} aria-current={current ? "page" : undefined} data-testid={`tab-${value}`}
                          className={`w-full text-left flex items-center justify-between gap-2 px-2.5 ${item} rounded-sm border-l-2 ${current ? "border-sky-600 bg-sky-50 text-navy-700 font-semibold" : "border-transparent text-slate-700 hover:bg-slate-50"}`}>
                          <span className="min-w-0 break-words">{label}</span>
                          {count > 0 && <span className="shrink-0 text-[10px] px-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid={`nav-pending-${value}`} title="Menunggu tindak lanjut">{count}</span>}
                        </button>
                      </li>
                    );
                  })}
                  {g.links.map(l => (
                    <li key={l.key}>
                      <Link to={l.to({ id, tab })} onClick={(e) => { if (!confirmLeave()) { e.preventDefault(); return; } onFollowLink?.(); }} data-testid={`nav-link-${l.key}`}
                        className={`w-full flex items-center justify-between gap-2 px-2.5 ${item} rounded-sm border-l-2 border-transparent text-slate-700 hover:bg-slate-50`}>
                        <span>{l.label}</span><ExternalLink size={12} className="shrink-0 text-muted-foreground" aria-label="halaman tingkat firma" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

// Wide screens (≥1280px): a sidebar column that can be hidden. Below that — laptops at 1024px, tablets, phones —
// the global sidebar leaves too little width for a second column, so a button opens the same
// list as a drawer. Both render NavList, so search, grouping and permissions cannot drift apart.
export function EngagementSidebar({ id, tab, onSelect, role, eng }) {
  const [hidden, setHidden] = useState(() => !!readStore().hidden);
  const setHiddenStored = (value) => { setHidden(value); writeStore({ ...readStore(), hidden: value }); };
  if (hidden) {
    return (
      <div className="hidden xl:block shrink-0">
        <button type="button" onClick={() => setHiddenStored(false)} aria-label="Tampilkan navigasi perikatan" data-testid="engagement-nav-show"
          className="sticky top-24 p-2 border border-border rounded-sm bg-white hover:bg-slate-50"><PanelLeftOpen size={16} /></button>
      </div>
    );
  }
  return (
    <aside className="hidden xl:flex flex-col w-60 shrink-0 sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto scroll-thin border border-border rounded-sm bg-white p-2" data-testid="engagement-nav">
      <div className="flex items-center justify-between mb-1 px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Menu perikatan</span>
        <button type="button" onClick={() => setHiddenStored(true)} aria-label="Sembunyikan navigasi perikatan" data-testid="engagement-nav-hide" className="p-1 hover:bg-slate-50 rounded-sm"><PanelLeftClose size={15} /></button>
      </div>
      <NavList id={id} tab={tab} onSelect={onSelect} role={role} pending={pendingCounts(eng)} />
    </aside>
  );
}

export function EngagementDrawer({ id, tab, onSelect, role, eng }) {
  const [open, setOpen] = useState(false);
  const pending = pendingCounts(eng);
  const total = Object.values(pending).reduce((a, b) => a + b, 0);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="engagement-nav-open"
        className="xl:hidden inline-flex items-center gap-2 min-h-11 px-3 border border-border rounded-sm bg-white text-sm font-medium">
        <Menu size={16} />Menu perikatan{total > 0 && <span className="text-[10px] px-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">{total}</span>}
      </button>
      {open && (
        <div className="xl:hidden fixed inset-0 z-40 flex" data-testid="engagement-drawer" role="dialog" aria-modal="true" aria-label="Menu perikatan">
          <div className="w-80 max-w-[88vw] bg-white h-full overflow-y-auto p-3 shadow-card-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-head font-semibold text-sm">Menu perikatan</span>
              <button type="button" aria-label="Tutup menu perikatan" onClick={() => setOpen(false)} className="min-h-11 min-w-11 flex items-center justify-center" data-testid="engagement-drawer-close"><X size={18} /></button>
            </div>
            <NavList id={id} tab={tab} role={role} pending={pending} compact onFollowLink={() => setOpen(false)}
              onSelect={(value) => { if (onSelect(value) !== false) setOpen(false); }} />
          </div>
          <button type="button" aria-label="Tutup menu perikatan" className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}
    </>
  );
}

export function EngagementBreadcrumb({ eng, tab }) {
  const group = groupOf(tab);
  return (
    <nav aria-label="Breadcrumb" className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-1 min-w-0" data-testid="engagement-breadcrumb">
      <Link to="/clients" className="hover:underline">Perikatan</Link><span aria-hidden>›</span>
      <span className="truncate max-w-[40vw]">{eng.client_name}</span><span aria-hidden>›</span>
      {group && <><span>{group.label}</span><span aria-hidden>›</span></>}
      <span className="text-foreground font-medium" aria-current="page">{labelOf(tab)}</span>
    </nav>
  );
}
